import { useState } from 'react';
import Button from '../../../components/form-control/Button';
import Input from '../../../components/form-control/Input';
import { Formik } from 'formik';
import * as Yup from 'yup';
import TextArea from '../../../components/form/TextArea';
import { createLeads } from '../../../api/requests';
import {
  CAR_INSURERS_LIST,
  GENDER,
  INSURANCE_TYPE,
  LEAD_SOURCE,
  LEAD_TAGS
} from '../../../resources/enums';
import { Link } from 'react-router-dom';
import { IcreateLead } from '../../../models/lead/ILead';
import { startCase } from 'lodash';
import SelectMenu from '../../../components/form-control/SelectMenu';
import {
  getFormattedLeadSource,
  getFormattedPhoneNumber,
  phoneValidationSchema,
  replaceSpacesWithUnderscores,
  showToast
} from '../../../utils/miscFunctions';
import DatePickerInput from '../../../components/form-control/DatePickerInput';
import moment from 'moment';
import { INPUT_DATE_FORMAT } from '../../../resources/constants';
import useRoute from '../../../hooks/routesHook';
import BackButton from '../../../components/BackButton';
import SelectDropdown, { ISelectDropdownItem } from '../../../components/form-control/SelectDropdown';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import { Insurer } from '../../../models/insurer/Insurer';
import { useQuery } from '@tanstack/react-query';
import { getAllInsurers } from '../../../services/insurer';

const LeadTags = Object.values(LEAD_TAGS).map((value) => value);
const InsuranceType = Object.values(INSURANCE_TYPE).map((value) => startCase(value));
const GenderList = Object.values(GENDER).map((value) => startCase(value));
const InsurersList = Object.values(CAR_INSURERS_LIST).map((value) => startCase(value))


const CreateLeads = () => {
  const [submitting, setSubmitting] = useState(false);
  const { getDashboardBase } = useRoute();
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const [insurers, setInsurers] = useState<Insurer[]>([]);

  const getInsurers = () => {
    getAllInsurers(countryId)
      .then((response: any) => {
        const _insurers = response?.data?.map((_insurer: any) => {
          return new Insurer(_insurer);
        });
        setInsurers(_insurers);
      })
      .catch((error: any) => {
        showToast.error(error?.description ?? error.message);
      })
      .finally(() => {
      });
  };

  useQuery({ queryKey: ['getInsurers', insurers, countryId], queryFn: getInsurers })

  let status;
  let tagsFromEnum = '';
  function selectedStatus(tag: string) {

    switch (tag) {
      case 'NEW':
        tagsFromEnum = 'COLD';
        break;
      case 'CONTACTED':
        tagsFromEnum = 'COLD';
        break;
      case 'QUALIFIED':
        tagsFromEnum = 'COLD'
        break;
      case 'UNRESPONSIVE':
        tagsFromEnum = 'COLD';
        break;
      case 'DISQUALIFIED':
        tagsFromEnum = 'UNRESPONSIVE'
        break;
      case 'IN_NEGOTIATION':
        tagsFromEnum = 'WARM';
        break;
      case 'NURTURING':
        tagsFromEnum = 'WARM'
        break;
      case 'CONVERTED':
        tagsFromEnum = 'CLOSED';
        break;
      default:
        break;
    }
    status = tagsFromEnum;
    return status;
  }

  function getLeadSource() {
    const type: any = [];

    for (const key in LEAD_SOURCE) {
      if (Object.prototype.hasOwnProperty.call(LEAD_SOURCE, key)) {
        type.push({
          name: getFormattedLeadSource(key),
          value: key
        });
      }
    }

    return type;
  }

  return (
    <Formik
      initialValues={{
        firstname: '',
        lastname: '',
        phone: '',
        email: '',
        currentInsurer: '',
        gender: '',
        insuranceType: '',
        expiryDate: null,
        leadStatus: '',
        leadSource: '',
        registrationNumber: '',
        description: '',
        tag: '',
        countryId: countryId,
      }}
      validationSchema={Yup.object().shape({
        firstname: Yup.string().required('First name is required'),
        lastname: Yup.string().required('Last name is required.'),
        email: Yup.string().email('Invalid email address'),
        phone: phoneValidationSchema(),
        gender: Yup.string().required('Gender is required.')
      })}
      onSubmit={(values, { resetForm }) => {

        const payload: IcreateLead = {
          ...values,
          leadStatus: selectedStatus(values.tag),
          phone: getFormattedPhoneNumber(values.phone),
          leadSource: replaceSpacesWithUnderscores(values.leadSource),
          expiryDate: values.expiryDate ? moment(values.expiryDate).format(INPUT_DATE_FORMAT) : '',
          countryId: countryId
        };
        setSubmitting(true);
        createLeads(payload)
          .then((response: any) => {
            showToast.success(response.message ?? 'Lead created successfully', );
            resetForm();
          })
          .catch((error: any) => {
            showToast.error(error.description, );
          })
          .finally(() => {
            setSubmitting(false);
          });
      }}>
      {({ values, errors, touched, handleSubmit, handleChange, setFieldValue }) => (
        <>
          <div className="flex items-center gap-8 mb-3 px-3">
            <Link to={`${getDashboardBase()}/leads`}>
              <BackButton />
            </Link>
            <h1 className="text-[32px] dark:text-darkBg-mainText">Add Lead</h1>
          </div>
          <form className="md:px-[45px] py-[30px] bg-white min-h-full rounded-lg shadow-md dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border" onSubmit={handleSubmit}>

            <div className="md:py-10 grid grid-cols-1 md:gap-8 items-center md:grid-cols-3 px-3">
              <div>
                <Input
                  type="text"
                  id="firstname"
                  name="firstname"
                  placeholder="Eg: Francis"
                  onChange={handleChange}
                  onBlur={handleChange}
                  value={values.firstname}
                  label="First Name"
                  required
                  error={touched.firstname ? errors.firstname : undefined}
                  helperText={touched.firstname ? errors.firstname : undefined}
                />
              </div>
              <div>
                <Input
                  type="text"
                  id="lastname"
                  name="lastname"
                  onChange={handleChange}
                  onBlur={handleChange}
                  value={values.lastname}
                  label="Last Name"
                  placeholder="Eg: Duah"
                  required
                  error={touched.lastname ? errors.lastname : undefined}
                  helperText={touched.lastname ? errors.lastname : undefined}
                />
              </div>
              <div>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  label="Email Address"
                  placeholder="Email"
                  onChange={handleChange}
                  onBlur={handleChange}
                  value={values.email}
                  error={touched.email ? errors.email : undefined}
                  helperText={touched.email ? errors.email : undefined}
                />
              </div>
              <div>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  onChange={handleChange}
                  onBlur={handleChange}
                  value={values.phone}
                  label="Phone"
                  placeholder="Eg: 0549237261"
                  required
                  error={touched.phone ? errors.phone : undefined}
                  helperText={touched.phone ? errors.phone : undefined}
                />
              </div>
              <div>
                <SelectMenu
                  onChange={(option: string | null) => setFieldValue('gender', option)}
                  listSelectedValue={values.gender}
                  name="gender"
                  label="Gender"
                  list={GenderList}
                  required
                  error={touched.gender ? errors.gender : undefined}
                  helperText={touched.gender ? errors.gender : undefined}
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="registrationNumber"
                  id="registrationNumber"
                  onChange={handleChange}
                  onBlur={handleChange}
                  value={values.registrationNumber}
                  label="Registration Number "
                  placeholder="Registration Number"
                />
              </div>
              <div>
                <SelectMenu
                  onChange={(option: string | null) => setFieldValue('tag', option)}
                  listSelectedValue={values.tag}
                  name="tag"
                  label="Tag"
                  list={LeadTags}
                  required
                  error={touched.tag ? errors.tag : undefined}
                  helperText={touched.tag ? errors.tag : undefined}
                />
              </div>
              <div>
                <Input
                  type="text"
                  id="status"
                  name="leadStatus"
                  onChange={handleChange}
                  onBlur={handleChange}
                  value={selectedStatus(values.tag)}
                  label="Stage"
                  placeholder="Stage"
                />
              </div>
              <div>
                <SelectDropdown
                  label="Current Insurer"
                  name="currentInsurer"
                  listSelectedValue={values.currentInsurer}
                  onChange={(val: ISelectDropdownItem | null) => {
                    setFieldValue('currentInsurer', val);
                  }}
                  search
                  searchPlaceHolder="Search by insurer ..."
                  list={insurers
                    ?.filter((insurer) => insurer.status === 'ACTIVE')
                    .map(
                      (insurer: Insurer) =>
                      ({
                        name: insurer.name,
                        value: insurer.name
                      } as ISelectDropdownItem)
                    )}
                  placeholder="Select insurer"
                />
              </div>
              <div>
                <SelectMenu
                  onChange={(option: string | null) => setFieldValue('insuranceType', option)}
                  listSelectedValue={values.insuranceType}
                  name="insuranceType"
                  label="Insurance Type"
                  list={InsuranceType}
                  sort={true}
                  error={touched.insuranceType ? errors.insuranceType : undefined}
                  helperText={touched.insuranceType ? errors.insuranceType : undefined}
                />
              </div>
              <div>
                <SelectDropdown
                  label="Source"
                  name="leadSource"
                  listSelectedValue={values.leadSource}
                  onChange={(val: ISelectDropdownItem | null) => {
                    setFieldValue('leadSource', val);
                  }}
                  search
                  searchPlaceHolder="Search by source ..."
                  list={getLeadSource().map(
                    (source: any) =>
                    ({
                      name: source.name,
                      value: source.name
                    } as ISelectDropdownItem)
                  )}
                  placeholder="Select source"
                />
              </div>
              <div>
                <DatePickerInput
                  id="expiryDate"
                  selected={values.expiryDate}
                  onChange={(date) => setFieldValue('expiryDate', date)}
                  dateFormat="MM-dd-yyyy"
                  label="Expiry Date"
                  showYearDropdown
                  allowEarlierSelections={false}
                />
              </div>
            </div>

            <div className="px-3">
              <TextArea
                onValueChanged={handleChange}
                name="description"
                id="description"
                value={values.description}
                label="Other Information"
                onFocusOut={() => { }}
              />
            </div>

            <div className="flex flex-col md:flex-row justify-end mt-10">
              <div>
                <Button
                  label="Save"
                  bgColor="primary"
                  type="submit"
                  disabled={submitting}
                  loading={submitting}
                  size='lg'
                />
              </div>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default CreateLeads;
